/**
 * Feedback API client for the memory & feedback system.
 *
 * Provides functions for submitting and retrieving tool execution feedback.
 */

import api from './client'

// ============================================================
// Request/Response Types
// ============================================================

export interface FeedbackRequest {
  audit_log_id: number
  rating: number // -1 (negative), 0 (neutral), 1 (positive)
  text_feedback?: string
}

export interface FeedbackResponse {
  ok: boolean
  feedback_id?: number
  error?: string
}

export interface AudioFeedbackResponse extends FeedbackResponse {
  transcription?: string
  audio_file_path?: string
}

export interface FeedbackSummary {
  tool_name: string
  total: number
  positive: number
  negative: number
  neutral: number
  satisfaction_rate: number
}

export interface FeedbackMetrics {
  total_feedback: number
  positive: number
  negative: number
  neutral: number
  overall_satisfaction: number
  tools_with_feedback: string[]
  low_satisfaction_tools: FeedbackSummary[]
}

export interface FeedbackItem {
  id: number
  timestamp: string
  audit_log_id: number | null
  session_id: string | null
  tool_name: string
  tool_args_hash: string | null
  rating: number
  feedback_type: string
  text_feedback: string | null
  audio_transcription: string | null
  audio_file_path: string | null
  user_query: string | null
  metadata: Record<string, unknown>
}

// ============================================================
// API Functions
// ============================================================

/**
 * Submit feedback for a tool execution.
 *
 * @param request - The feedback request with audit_log_id, rating, and optional text
 * @returns Response with ok status and feedback_id on success
 */
export async function submitFeedback(
  request: FeedbackRequest
): Promise<FeedbackResponse> {
  try {
    const response = await api.post<FeedbackResponse>('/feedback', request)
    return response.data
  } catch (error) {
    console.error('Error submitting feedback:', error)
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Failed to submit feedback',
    }
  }
}

/**
 * Submit audio feedback with transcription.
 *
 * @param audioBlob - The recorded audio blob
 * @param auditLogId - The audit log ID to associate feedback with
 * @param rating - The rating (-1, 0, 1)
 * @param textFeedback - Optional additional text feedback
 * @returns Response with transcription and feedback_id on success
 */
export async function submitAudioFeedback(
  audioBlob: Blob,
  auditLogId: number,
  rating: number,
  textFeedback?: string
): Promise<AudioFeedbackResponse> {
  try {
    // Convert blob to base64
    const base64Audio = await blobToBase64(audioBlob)

    const response = await api.post<AudioFeedbackResponse>('/feedback/audio', {
      audit_log_id: auditLogId,
      rating,
      audio_data: base64Audio,
      mime_type: audioBlob.type || 'audio/webm',
      text_feedback: textFeedback,
    })

    return response.data
  } catch (error) {
    console.error('Error submitting audio feedback:', error)
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Failed to submit audio feedback',
    }
  }
}

/**
 * Get feedback summary for a specific tool.
 *
 * @param toolName - The name of the tool
 * @returns Feedback summary with counts and satisfaction rate
 */
export async function getFeedbackSummary(
  toolName: string
): Promise<{ ok: boolean; summary?: FeedbackSummary; error?: string }> {
  try {
    const response = await api.get<{ ok: boolean; summary: FeedbackSummary }>(
      `/feedback/tool/${encodeURIComponent(toolName)}`
    )
    return response.data
  } catch (error) {
    console.error('Error getting feedback summary:', error)
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Failed to get feedback summary',
    }
  }
}

/**
 * Get feedback metrics for the dashboard.
 *
 * @returns Overall feedback metrics including low satisfaction tools
 */
export async function getFeedbackMetrics(): Promise<{
  ok: boolean
  metrics?: FeedbackMetrics
  error?: string
}> {
  try {
    const response = await api.get<{ ok: boolean; metrics: FeedbackMetrics }>(
      '/feedback/metrics'
    )
    return response.data
  } catch (error) {
    console.error('Error getting feedback metrics:', error)
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Failed to get feedback metrics',
    }
  }
}

/**
 * Get list of feedback items with optional filters.
 *
 * @param options - Filter options
 * @returns List of feedback items
 */
export async function getFeedbackList(options?: {
  tool_name?: string
  rating?: number
  limit?: number
  offset?: number
}): Promise<{ ok: boolean; feedback?: FeedbackItem[]; error?: string }> {
  try {
    const params = new URLSearchParams()
    if (options?.tool_name) params.append('tool_name', options.tool_name)
    if (options?.rating !== undefined) params.append('rating', String(options.rating))
    if (options?.limit) params.append('limit', String(options.limit))
    if (options?.offset) params.append('offset', String(options.offset))

    const response = await api.get<{ ok: boolean; feedback: FeedbackItem[] }>(
      `/feedback?${params.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error getting feedback list:', error)
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Failed to get feedback list',
    }
  }
}

// ============================================================
// Utility Functions
// ============================================================

/**
 * Convert a Blob to a base64 string.
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result as string
      // Extract base64 data from data URL
      const base64 = result.split(',')[1] || result
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}
